/*!
 * TKO JSX Rendering 🥊  @tko/utils.jsx@4.0.0-alpha8
 * (c) The Knockout.js Team - https://tko.io
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { LifeCycle } from '@tko/lifecycle';
import { cleanNode, safeStringify, isThenable } from '@tko/utils';
import { applyBindings, contextFor } from '@tko/bind';
import { isObservable, unwrap, observable } from '@tko/observable';
import { NativeProvider } from '@tko/provider.native';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __values(o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

var ORIGINAL_JSX_SYM = Symbol('Knockout - Original JSX');
var NAMESPACES = {
    svg: 'http://www.w3.org/2000/svg',
    html: 'http://www.w3.org/1999/xhtml',
    xml: 'http://www.w3.org/XML/1998/namespace',
    xlink: 'http://www.w3.org/1999/xlink',
    xmlns: 'http://www.w3.org/2000/xmlns/'
};
/**
 * JSX object from a pre-processor.
 * @typedef {Object} JSX
 * @property {string} elementName becomes the `tagName`
 * @property {Array.<JSX>} children
 * @property {object} attributes
 */
/**
 * Observe a variety of possible cases from JSX, modifying the
 * `parentNode` at `insertBefore` with the result.
 */
var JsxObserver = /** @class */ (function (_super) {
    __extends(JsxObserver, _super);
    /**
     * @param {any} jsxOrObservable take a long list of permutations
     */
    function JsxObserver(jsxOrObservable, parentNode, insertBefore, xmlns) {
        if (insertBefore === void 0) { insertBefore = null; }
        var _this = _super.call(this) || this;
        var parentNodeIsComment = parentNode.nodeType === 8;
        var parentNodeTarget = _this.getParentTarget(parentNode);
        if (isObservable(jsxOrObservable)) {
            jsxOrObservable.extend({ trackArrayChanges: true });
            _this.subscribe(jsxOrObservable, _this.observableArrayChange, 'arrayChange');
            if (!insertBefore) {
                var insertAt = parentNodeIsComment ? parentNode.nextSibling : null;
                insertBefore = document.createComment('O');
                parentNodeTarget.insertBefore(insertBefore, insertAt);
            }
            else {
                _this.adoptedInsertBefore = true;
            }
        }
        if (parentNodeIsComment && !insertBefore) {
            // Typcially: insertBefore becomes <!-- /ko -->
            insertBefore = parentNode.nextSibling;
            // Mark this so we don't remove the next node - since we didn't create it.
            _this.adoptedInsertBefore = true;
        }
        _this.anchorTo(insertBefore || parentNode);
        Object.assign(_this, {
            insertBefore: insertBefore,
            parentNode: parentNode,
            parentNodeTarget: parentNodeTarget,
            xmlns: xmlns,
            nodeArrayOrObservableAtIndex: [],
            performingInitialAdditions: true,
            subscriptionsForNode: new Map()
        });
        var jsx = unwrap(jsxOrObservable);
        if (jsx !== null && jsx !== undefined) {
            _this.observableArrayChange(_this.createInitialAdditions(jsx));
        }
        _this.performingInitialAdditions = false;
        return _this;
    }
    /**
     * @param {HMTLElement|Comment|HTMLTemplateElement} parentNode
     */
    JsxObserver.prototype.getParentTarget = function (parentNode) {
        if ('content' in parentNode) {
            return parentNode.content;
        }
        if (parentNode.nodeType === 8) {
            return parentNode.parentNode;
        }
        return parentNode;
    };
    JsxObserver.prototype.dispose = function () {
        var e_1, _a;
        _super.prototype.dispose.call(this);
        var ib = this.insertBefore;
        var insertBeforeIsChild = ib && this.parentNodeTarget === ib.parentNode;
        if (insertBeforeIsChild && !this.adoptedInsertBefore) {
            this.parentNodeTarget.removeChild(ib);
        }
        this.removeAllPriorNodes();
        Object.assign(this, {
            parentNode: null,
            parentNodeTarget: null,
            insertBefore: null,
            nodeArrayOrObservableAtIndex: []
        });
        try {
            for (var _b = __values(this.subscriptionsForNode.values()), _c = _b.next(); !_c.done; _c = _b.next()) {
                var subscriptions = _c.value;
                subscriptions.forEach(function (s) { return s.dispose(); });
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b["return"])) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        this.subscriptionsForNode.clear();
    };
    JsxObserver.prototype.createInitialAdditions = function (possibleIterable) {
        var status = 'added';
        if (typeof possibleIteratable === 'object' &&
            posibleIterable !== null &&
            Symbol.iterator in possibleIterable) {
            possibleIterable = __spread(possibleIterable);
        }
        return Array.isArray(possibleIterable)
            ? possibleIterable.map(function (value, index) { return ({ index: index, status: status, value: value }); })
            : [{ status: status, index: 0, value: possibleIterable }];
    };
    /**
     * Note: array change notification indexes are:
     *   - to the original array indexes for deletes
     *   - to the new array indexes for adds
     *   - sorted by index in ascending order
     */
    JsxObserver.prototype.observableArrayChange = function (changes) {
        var _this = this;
        var adds = [];
        var dels = [];
        for (var index in changes) {
            var change = changes[index];
            if (change.status === 'added') {
                adds.push([change.index, change.value]);
            }
            else {
                dels.unshift([change.index, change.value]);
            }
        }
        dels.forEach(function (change) { return _this.delChange.apply(_this, __spread(change)); });
        adds.forEach(function (change) { return _this.addChange.apply(_this, __spread(change)); });
    };
    /**
     * Add a change at the given index.
     *
     * @param {int} index
     * @param {string|object|Array|Observable.string|Observable.Array|Obseravble.object} jsx
     */
    JsxObserver.prototype.addChange = function (index, jsx) {
        this.nodeArrayOrObservableAtIndex.splice(index, 0, this.injectNode(jsx, this.lastNodeFor(index)));
    };
    JsxObserver.prototype.injectNode = function (jsx, nextNode) {
        var _this = this;
        var e_2, _a;
        var nodeArrayOrObservable;
        if (isObservable(jsx)) {
            var _b = this, parentNode = _b.parentNode, xmlns = _b.xmlns;
            var observer = new JsxObserver(jsx, parentNode, nextNode, xmlns);
            nodeArrayOrObservable = [observer];
        }
        else {
            var $context = contextFor(this.parentNode);
            var isInsideTemplate = 'content' in this.parentNode;
            var shouldApplyBindings = $context && !isInsideTemplate &&
                !this.performingInitialAdditions;
            if (Array.isArray(jsx)) {
                nodeArrayOrObservable = jsx.map(function (j) { return _this.anyToNode(j); });
            }
            else {
                nodeArrayOrObservable = [this.anyToNode(jsx)];
            }
            try {
                for (var nodeArrayOrObservable_1 = __values(nodeArrayOrObservable), nodeArrayOrObservable_1_1 = nodeArrayOrObservable_1.next(); !nodeArrayOrObservable_1_1.done; nodeArrayOrObservable_1_1 = nodeArrayOrObservable_1.next()) {
                    var node = nodeArrayOrObservable_1_1.value;
                    this.parentNodeTarget.insertBefore(node, nextNode);
                    if (shouldApplyBindings && this.canApplyBindings(node)) {
                        applyBindings($context, node);
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (nodeArrayOrObservable_1_1 && !nodeArrayOrObservable_1_1.done && (_a = nodeArrayOrObservable_1["return"])) _a.call(nodeArrayOrObservable_1);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
        return nodeArrayOrObservable;
    };
    /**
     * True when Node is a type suitable for applyBindings i.e. a HTMLElement
     * or a Comment.
     * @param {Node} node
     */
    JsxObserver.prototype.canApplyBindings = function (node) {
        return node.nodeType === 1 || node.nodeType === 8;
    };
    JsxObserver.prototype.delChange = function (index) {
        this.removeNodeArrayOrObservable(this.nodeArrayOrObservableAtIndex[index]);
        this.nodeArrayOrObservableAtIndex.splice(index, 1);
    };
    JsxObserver.prototype.getSubscriptionsForNode = function (node) {
        if (!this.subscriptionsForNode.has(node)) {
            var subscriptions = [];
            this.subscriptionsForNode.set(node, subscriptions);
            return subscriptions;
        }
        return this.subscriptionsForNode.get(node);
    };
    JsxObserver.prototype.isJsx = function (jsx) {
        return typeof jsx.elementName === 'string' &&
            'children' in jsx &&
            'attributes' in jsx;
    };
    /**
     * @param {any} value acceptable to turn into a Node
     *
     * The one thing `any` cannot be here is an Array or Observable; both those
     * cases are handled with new JsxObservers.
     */
    JsxObserver.prototype.anyToNode = function (any) {
        if (isThenable(any)) {
            return this.futureJsxNode(any);
        }
        switch (typeof any) {
            case 'object':
                if (any instanceof Error) {
                    return document.createComment(any.toString());
                }
                break;
            case 'function': return this.anyToNode(any());
            case 'undefined':
            case 'Error':
            case 'symbol':
                return document.createComment(String(any));
            case 'string': return document.createTextNode(any);
            case 'boolean':
            case 'number':
            case 'bigint':
            default:
                return document.createTextNode(String(any));
        }
        if (any === null) {
            return document.createComment(String(any));
        }
        if (any instanceof Node) {
            return this.cloneNode(any);
        }
        if (!this.isJsx(any)) {
            return document.createComment(safeStringify(any));
        }
        return this.jsxToNode(any);
    };
    /**
     * Clone a node; if that node was originally from JSX, we clone from there
     * so we preserve binding handlers.
     *
     * @param {HTMLElement} node
     */
    JsxObserver.prototype.cloneNode = function (node) {
        if (ORIGINAL_JSX_SYM in node) {
            return this.jsxToNode(node[ORIGINAL_JSX_SYM]);
        }
        else {
            return node.cloneNode(true);
        }
    };
    /**
     * @param {JSX} jsx to convert to a node.
     */
    JsxObserver.prototype.jsxToNode = function (jsx) {
        var _this = this;
        var xmlns = jsx.attributes.xmlns || NAMESPACES[jsx.elementName] || this.xmlns;
        var node = document.createElementNS(xmlns || NAMESPACES.html, jsx.elementName);
        /** Slots need to be able to replicate with the attributes, which
         *  are not preserved when cloning from template nodes. */
        node[ORIGINAL_JSX_SYM] = jsx;
        if (isObservable(jsx.attributes)) {
            var subscriptions = this.getSubscriptionsForNode(node);
            subscriptions.push(jsx.attributes.subscribe(function (attrs) {
                _this.updateAttributes(node, unwrap(attrs));
            }));
        }
        this.updateAttributes(node, unwrap(jsx.attributes));
        this.addDisposable(new JsxObserver(jsx.children, node, null, xmlns));
        return node;
    };
    JsxObserver.prototype.futureJsxNode = function (promise) {
        var obs = observable();
        promise.then(obs)["catch"](function (e) { return obs(e instanceof Error ? e : Error(e)); });
        var jo = new JsxObserver(obs, this.parentNode, null, this.xmlns);
        this.addDisposable(jo);
        return jo.insertBefore;
    };
    JsxObserver.prototype.updateAttributes = function (node, attributes) {
        var _this = this;
        var e_3, _a, e_4, _b;
        var subscriptions = this.getSubscriptionsForNode(node);
        var toRemove = new Set(__spread(node.attributes).map(function (n) { return n.name; }));
        var _loop_1 = function (name_1, value) {
            toRemove["delete"](name_1);
            if (isObservable(value)) {
                subscriptions.push(value.subscribe(function (attr) { return _this.setNodeAttribute(node, name_1, attr); }));
            }
            this_1.setNodeAttribute(node, name_1, value);
        };
        var this_1 = this;
        try {
            for (var _c = __values(Object.entries(attributes || {})), _d = _c.next(); !_d.done; _d = _c.next()) {
                var _e = __read(_d.value, 2), name_1 = _e[0], value = _e[1];
                _loop_1(name_1, value);
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c["return"])) _a.call(_c);
            }
            finally { if (e_3) throw e_3.error; }
        }
        try {
            for (var toRemove_1 = __values(toRemove), toRemove_1_1 = toRemove_1.next(); !toRemove_1_1.done; toRemove_1_1 = toRemove_1.next()) {
                var name_2 = toRemove_1_1.value;
                this.setNodeAttribute(node, name_2, undefined);
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (toRemove_1_1 && !toRemove_1_1.done && (_b = toRemove_1["return"])) _b.call(toRemove_1);
            }
            finally { if (e_4) throw e_4.error; }
        }
    };
    /**
     * See https://stackoverflow.com/a/52572048
     * @param {string} attr element attribute
     * @return {string} namespace argument for setAtttributeNS
     */
    JsxObserver.prototype.getNamespaceOfAttribute = function (attr) {
        var _a = __read(attr.split(':')), prefix = _a[0], unqualifiedName = _a.slice(1);
        if (prefix === 'xmlns' || (unqualifiedName.length && NAMESPACES[prefix])) {
            return NAMESPACES[prefix];
        }
        return null;
    };
    /**
     *
     * @param {HTMLElement} node
     * @param {string} name
     * @param {any} valueOrObservable
     */
    JsxObserver.prototype.setNodeAttribute = function (node, name, valueOrObservable) {
        var _this = this;
        var value = unwrap(valueOrObservable);
        NativeProvider.addValueToNode(node, name, valueOrObservable);
        if (value === undefined) {
            node.removeAttributeNS(null, name);
        }
        else if (isThenable(valueOrObservable)) {
            Promise.resolve(valueOrObservable)
                .then(function (v) { return _this.setNodeAttribute(node, name, v); });
        }
        else {
            var ns = this.getNamespaceOfAttribute(name);
            node.setAttributeNS(ns, name, String(value));
        }
    };
    /**
     * @param {int} index
     * @return {Comment} that immediately precedes this.
     */
    JsxObserver.prototype.lastNodeFor = function (index) {
        var nodesAtIndex = this.nodeArrayOrObservableAtIndex[index] || [];
        var _a = __read(nodesAtIndex.slice(-1), 1), lastNodeOfPrior = _a[0];
        var insertBefore = lastNodeOfPrior instanceof JsxObserver
            ? lastNodeOfPrior.insertBefore : lastNodeOfPrior || this.insertBefore;
        if (insertBefore) {
            return insertBefore.parentNode ? insertBefore : null;
        }
        return null;
    };
    JsxObserver.prototype.removeAllPriorNodes = function () {
        var nodeArrayOrObservableAtIndex = this.nodeArrayOrObservableAtIndex;
        while (nodeArrayOrObservableAtIndex.length) {
            this.removeNodeArrayOrObservable(nodeArrayOrObservableAtIndex.pop());
        }
    };
    JsxObserver.prototype.removeNodeArrayOrObservable = function (nodeArrayOrObservable) {
        var e_5, _a;
        try {
            for (var nodeArrayOrObservable_2 = __values(nodeArrayOrObservable), nodeArrayOrObservable_2_1 = nodeArrayOrObservable_2.next(); !nodeArrayOrObservable_2_1.done; nodeArrayOrObservable_2_1 = nodeArrayOrObservable_2.next()) {
                var nodeOrObservable = nodeArrayOrObservable_2_1.value;
                if (nodeOrObservable instanceof JsxObserver) {
                    nodeOrObservable.dispose();
                    continue;
                }
                var node = nodeOrObservable;
                node[ORIGINAL_JSX_SYM] = 'disposed';
                this.detachAndDispose(node);
                var subscriptions = this.subscriptionsForNode.get(node);
                if (subscriptions) {
                    subscriptions.forEach(function (s) { return s.dispose(); });
                    this.subscriptionsForNode["delete"](node);
                }
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (nodeArrayOrObservable_2_1 && !nodeArrayOrObservable_2_1.done && (_a = nodeArrayOrObservable_2["return"])) _a.call(nodeArrayOrObservable_2);
            }
            finally { if (e_5) throw e_5.error; }
        }
    };
    /**
     * Detach the given node, and dispose of its children.
     *
     * The cleaning can trigger a lot of garbage collection, so we defer that.
     */
    JsxObserver.prototype.detachAndDispose = function (node) {
        node.remove();
        requestAnimationFrame(function () { return cleanNode(node); });
    };
    return JsxObserver;
}(LifeCycle));

/**
 *
 * @param {any} possibleJsx Test whether this value is JSX.
 *
 * True for
 *    { elementName }
 *    [{elementName}]
 *    observable({elementName} | [])
 *
 * Any observable will return truthy if its value is an array that doesn't
 * contain HTML elements.  Template nodes should not be observable unless they
 * are JSX.
 *
 * There's a bit of guesswork here that we could nail down with more test cases.
 */
function maybeJsx(possibleJsx) {
    if (isObservable(possibleJsx)) {
        return true;
    }
    var value = unwrap(possibleJsx);
    if (!value) {
        return false;
    }
    if (value.elementName) {
        return true;
    }
    if (!Array.isArray(value) || !value.length) {
        return false;
    }
    if (value[0] instanceof window.Node) {
        return false;
    }
    return true;
}
function getOriginalJsxForNode(node) {
    return node[ORIGINAL_JSX_SYM];
}
/**
 * Convert JSX into an object that can be consumed by TKO.
 * Mimics React.createElement
 * @param {string} e tagName of the element
 * @param {object|null} a attributes of the element
 * @param  {...string|object} c children of the element
 */
function createElement(e, a) {
    var c = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        c[_i - 2] = arguments[_i];
    }
    return {
        elementName: e,
        attributes: a || {},
        children: __spread(c)
    };
}

export { getOriginalJsxForNode, maybeJsx, createElement, JsxObserver };
//# sourceMappingURL=utils.jsx.js.map
